import React from "react";
import { useUserData } from "@me8eon/react_authentication";
import { useTranslation } from "@me8eon/translation";
import { useLanguage } from "@me8eon/language";
import {
    DashboardTabKey,
    SelectedCategory,
    fmt,
} from "../poData";
import RequestCard from "../components/RequestCard";
import FeedbackPie from "../components/FeedbackPie";
import DashboardSkeleton from "../components/DashboardSkeleton";
import TabError from "../components/TabError";
import UniqueUsersSection from "../components/UniqueUsersSection";
import { DashboardRange } from "../../../services/dashboardApi";
import { useDashboardData } from "../useDashboardData";
import { EonUiTabs, EonUiTab } from "@eon-ui/eon-ui-components-react";
import ActionsForYou from "../components/ActionsForYou";
import { ROLE_PRODUCT_TEAM_DASHBOARD_PO_ACTIONS_FOR_YOU } from "../ProductOwnerDashboard.constants";

type Props = {
    onNavigate: (tab: DashboardTabKey, category?: SelectedCategory) => void;
    range: DashboardRange;
    periodDays: number;
    isCustomPeriod: boolean;
    categoryFilter: SelectedCategory[];
    companyFilter: string[];
};

const OverviewTab: React.FC<Props> = ({ onNavigate, range, periodDays, isCustomPeriod, categoryFilter, companyFilter }) => {
    const t = useTranslation();
    const [language] = useLanguage();
    const tp = (key: string) => t(`productDashboard.overview.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    // Logged-in user's first name for the greeting. Uses useUserData() from the app's
    // AuthenticationProvider — the same context that powers useJwtToken here and the header
    // name. Prefer the firstName claim (given_name); else parse `name` ("Last, First" → First,
    // or "First Last" → First). Empty until logged in.
    const { firstName: firstNameClaim, name, actualRoles } = useUserData();
    const showActionsForYou = actualRoles?.includes(ROLE_PRODUCT_TEAM_DASHBOARD_PO_ACTIONS_FOR_YOU);
    const firstName = (() => {
        const claim = (firstNameClaim ?? "").trim();
        if (claim) return claim;
        const full = (name ?? "").trim();
        if (!full) return "";
        return (full.includes(",") ? full.split(",")[1] : full.split(" ")[0])?.trim() ?? "";
    })();

    // Time-of-day greeting from the user's local hour (evaluated at render).
    const greeting = (() => {
        const h = new Date().getHours();
        if (h >= 5 && h < 12) return tp("greetingMorning");
        if (h >= 12 && h < 17) return tp("greetingAfternoon");
        if (h >= 17 && h < 21) return tp("greetingEvening");
        return tp("greetingNight");
    })();
    const { kpis, callbackRequests, liveChatRequests, uniqueUsers, loading, error } = useDashboardData(range, categoryFilter, companyFilter);

    return (
        <div className="po-overview">
            <h1 className="po-greeting">{greeting}{firstName ? `, ${firstName}` : ""}.</h1>
            <p className="po-greeting__sub">{tp("subtitle")}</p>

            {/* ══════════ AI INSIGHT banner (full-width, top) ══════════ */}
            {/* <div className="po-insight">
                <span className="po-insight__icon">ⓘ</span>
                <span>
                    <strong>AI Insight:</strong> {AI_INSIGHT}
                </span>
            </div> */}
            <EonUiTabs
                active-tab-index={0}
                key={language}
            >
                <EonUiTab header={tp("tabOverview")} tab-id="overview">
                    <div className="po-overview__main">
                        {/* ══════════ MAIN ▸ KPI GRID (2×3 cards) + chart — skeleton while loading, error if the fetch failed ══════════ */}
                        {loading ? (
                            <DashboardSkeleton />
                        ) : error ? (
                            <TabError />
                        ) : (
                            <>
                                <div className="po-kpis">
                                    {/* KPI card 1 — Total Queries (click → Total Queries tab) */}
                                    <button
                                        type="button"
                                        className="po-card po-kpi po-kpi--clickable"
                                        onClick={() =>
                                            onNavigate("totalQueries")
                                        }
                                        title={tp(
                                            "kpiTooltips.totalQueries"
                                        )}
                                    >
                                        <div className="po-kpi__head">
                                            <span className="po-kpi__label">
                                                {tp("kpiTotalQueries")}
                                            </span>
                                            {/* Trend delta hidden for now */}
                                            {/* <span className="po-trend po-trend--up">{kpis.totalQueriesDelta == null ? "—" : `↗ +${kpis.totalQueriesDelta}%`}</span> */}
                                        </div>
                                        <div className="po-kpi__value">
                                            {fmt(kpis.totalQueries)}
                                        </div>
                                        <div className="po-kpi__meta">
                                            {tc(isCustomPeriod ? "for" : "last")} {periodDays}{" "}
                                            {tc(periodDays === 1 ? "day" : "days")} · {kpis.categories}{" "}
                                            {tc("categories")}
                                        </div>
                                        <span className="po-link po-link--cue">
                                            {tp("viewBreakdown")}
                                        </span>
                                    </button>

                                    {/* KPI card 2 — Incidents Raised (click → Incidents tab; live Snowflake status split) */}
                                    <button
                                        type="button"
                                        className="po-card po-kpi po-kpi--clickable"
                                        onClick={() =>
                                            onNavigate("incidents")
                                        }
                                        title={tp(
                                            "kpiTooltips.incidentsRaised"
                                        )}
                                    >
                                        <div className="po-kpi__head">
                                            <span className="po-kpi__label">
                                                {tp("kpiIncidentsRaised")}
                                            </span>
                                            {/* Trend delta hidden for now */}
                                            {/* <span className="po-trend po-trend--bad">{kpis.incidentsDelta == null ? "—" : `↗ +${kpis.incidentsDelta}%`}</span> */}
                                        </div>
                                        <div className="po-kpi__value">
                                            {kpis.incidentsRaised}
                                        </div>
                                        <div className="po-kpi__meta">
                                            {tc(isCustomPeriod ? "for" : "last")} {periodDays}{" "}
                                            {tc(periodDays === 1 ? "day" : "days")}
                                        </div>
                                        <span className="po-link po-link--cue">
                                            {tp("viewAllIncidents")}
                                        </span>
                                    </button>

                                    {/* KPI card 6 — Feedback-coverage donut (clickable → User Sentiment; category-aware) */}
                                    <FeedbackPie
                                        totalQueries={kpis.totalQueries}
                                        likes={kpis.likes}
                                        dislikes={kpis.dislikes}
                                        periodDays={periodDays}
                                        isCustomPeriod={isCustomPeriod}
                                        tooltip={tp(
                                            "kpiTooltips.feedbackCoverage"
                                        )}
                                        onClick={() =>
                                            onNavigate("userSentiment")
                                        }
                                    />

                                    {/* KPI cards 4 & 5 — Callback + Live Chat requests (Snowflake→Postgres, split by language) */}
                                    <RequestCard
                                        label={tp("kpiCallback")}
                                        tooltip={tp("kpiTooltips.callback")}
                                        data={callbackRequests}
                                        periodDays={periodDays}
                                        isCustomPeriod={isCustomPeriod}
                                        onViewBreakdown={() => onNavigate("callbackRequests")}
                                    />
                                    <RequestCard
                                        label={tp("kpiLiveChat")}
                                        tooltip={tp("kpiTooltips.liveChat")}
                                        data={liveChatRequests}
                                        periodDays={periodDays}
                                        isCustomPeriod={isCustomPeriod}
                                        onViewBreakdown={() => onNavigate("liveChatRequests")}
                                    />
                                    {/* KPI card 3 — placeholder for an upcoming feature */}
                                    <div
                                        className="po-card po-kpi po-kpi--upcoming"
                                        title={tp("kpiTooltips.upcoming")}
                                    >
                                        <div className="po-kpi__head">
                                            <span className="po-kpi__label">
                                                {tp("kpiUpcoming")}
                                            </span>
                                        </div>
                                        <div className="po-upcoming">
                                            <span className="po-upcoming__icon">
                                                🚧
                                            </span>
                                            <span className="po-upcoming__title">
                                                {tp("upcomingTitle")}
                                            </span>
                                            <span className="po-upcoming__sub">
                                                {tp("upcomingSub")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <UniqueUsersSection data={uniqueUsers} />
                            </>
                        )}
                    </div>
                </EonUiTab>
                {showActionsForYou && <EonUiTab header={tp("tabActionsForYou")} tab-id="actionsForYou">
                    <ActionsForYou />
                </EonUiTab>}
            </EonUiTabs>
        </div>
    );
};

export default OverviewTab;
